/**
 * Design Tokens for Hivetivity
 * Standardized color palette and design system
 */

export const colors = {
  // Brand Colors (Hivetivity Blue)
  primary: {
    50: '#f0f9ff',
    100: '#e0f2fe', 
    200: '#bae6fd',
    300: '#7dd3fc',
    400: '#38bdf8',
    500: '#4ba3c7', // Main Hivetivity Blue
    600: '#3c82a0',
    700: '#2d6179',
    800: '#1e4052',
    900: '#0f202b',
  },

  // Status Colors (Semantic)
  success: {
    50: '#f0fdf4',
    100: '#dcfce7',
    200: '#bbf7d0',
    300: '#86efac',
    400: '#4ade80',
    500: '#22c55e',
    600: '#16a34a',
    700: '#15803d',
    800: '#166534',
    900: '#14532d',
  },

  warning: {
    50: '#fffbeb',
    100: '#fef3c7',
    200: '#fde68a',
    300: '#fcd34d',
    400: '#fbbf24',
    500: '#f59e0b',
    600: '#d97706',
    700: '#b45309',
    800: '#92400e',
    900: '#78350f',
  },

  error: {
    50: '#fef2f2',
    100: '#fee2e2',
    200: '#fecaca',
    300: '#fca5a5',
    400: '#f87171',
    500: '#ef4444',
    600: '#dc2626',
    700: '#b91c1c',
    800: '#991b1b',
    900: '#7f1d1d',
  },

  info: {
    50: '#f0f9ff',
    100: '#e0f2fe', 
    200: '#bae6fd',
    300: '#7dd3fc',
    400: '#38bdf8',
    500: '#4ba3c7', // Same as primary for consistency
    600: '#3c82a0',
    700: '#2d6179',
    800: '#1e4052',
    900: '#0f202b',
  },

  // Neutral Colors
  neutral: {
    50: '#f9fafb',
    100: '#f3f4f6',
    200: '#e5e7eb',
    300: '#d1d5db',
    400: '#9ca3af',
    500: '#6b7280',
    600: '#4b5563',
    700: '#374151',
    800: '#1f2937',
    900: '#111827',
  },

  // Functional Colors (for specific UI elements)
  active: {
    bg: '#dcfce7',
    text: '#166534',
    border: '#bbf7d0',
  },

  inactive: {
    bg: '#f3f4f6',
    text: '#6b7280',
    border: '#e5e7eb',
  },

  pending: {
    bg: '#fef3c7',
    text: '#92400e',
    border: '#fde68a',
  },
} as const;

export const statusVariants = {
  success: {
    bg: colors.success[50],
    text: colors.success[800],
    border: colors.success[200],
  },
  warning: {
    bg: colors.warning[50],
    text: colors.warning[800],
    border: colors.warning[200],
  },
  error: {
    bg: colors.error[50],
    text: colors.error[800],
    border: colors.error[200],
  },
  info: {
    bg: colors.info[50],
    text: colors.info[800],
    border: colors.info[200],
  },
  neutral: {
    bg: colors.neutral[50],
    text: colors.neutral[600],
    border: colors.neutral[200],
  },
} as const;

// Component-specific color variants
export const badgeVariants = {
  // Status badges using design tokens
  active: {
    bg: colors.success[50],
    text: colors.success[700],
    border: colors.success[200],
    classes: 'bg-green-50 text-green-700 border-green-200',
  },
  inactive: {
    bg: colors.neutral[50],
    text: colors.neutral[500],
    border: colors.neutral[200],
    classes: 'bg-gray-50 text-gray-500 border-gray-200',
  },
  waitlisted: {
    bg: colors.warning[50],
    text: colors.warning[700],
    border: colors.warning[200],
    classes: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  },
  withdrawn: {
    bg: colors.error[50],
    text: colors.error[700],
    border: colors.error[200],
    classes: 'bg-red-50 text-red-700 border-red-200',
  },
  completed: {
    bg: colors.info[50],
    text: colors.info[700],
    border: colors.info[200],
    classes: 'bg-blue-50 text-blue-700 border-blue-200',
  },
  pending: {
    bg: colors.warning[50],
    text: colors.warning[600],
    border: colors.warning[200],
    classes: 'bg-yellow-50 text-yellow-600 border-yellow-200',
  },
} as const;

// Interactive state colors
export const interactionStates = {
  hover: {
    primary: colors.primary[600],
    secondary: colors.neutral[100],
    success: colors.success[600],
    warning: colors.warning[600],
    error: colors.error[600],
  },
  focus: {
    ring: colors.primary[500],
    offset: colors.neutral[50],
  },
  active: {
    primary: colors.primary[700],
    secondary: colors.neutral[200],
  },
  disabled: {
    bg: colors.neutral[100],
    text: colors.neutral[400],
    border: colors.neutral[200],
  },
} as const;

// Typography system
export const typography = {
  sizes: {
    xs: 'text-xs',     // 12px
    sm: 'text-sm',     // 14px
    base: 'text-base', // 16px
    lg: 'text-lg',     // 18px
    xl: 'text-xl',     // 20px
    '2xl': 'text-2xl', // 24px
    '3xl': 'text-3xl', // 30px
  },
  weights: {
    normal: 'font-normal',   // 400
    medium: 'font-medium',   // 500
    semibold: 'font-semibold', // 600
    bold: 'font-bold',       // 700
  },
  // Component-specific typography
  components: {
    cardTitle: 'text-lg font-semibold',
    cardDescription: 'text-sm text-muted-foreground',
    pageTitle: 'text-2xl font-bold',
    sectionTitle: 'text-xl font-semibold',
    label: 'text-sm font-medium',
    body: 'text-sm',
    caption: 'text-xs text-muted-foreground',
  },
} as const;

// Spacing system extensions
export const spacing = {
  component: {
    xs: '0.25rem', // 4px
    sm: '0.5rem',  // 8px
    md: '1rem',    // 16px
    lg: '1.5rem',  // 24px
    xl: '2rem',    // 32px
    '2xl': '3rem', // 48px
  },
  // Icon sizes
  icons: {
    xs: 'h-3 w-3',   // 12px
    sm: 'h-4 w-4',   // 16px
    md: 'h-5 w-5',   // 20px
    lg: 'h-6 w-6',   // 24px
    xl: 'h-8 w-8',   // 32px
  },
} as const;

// Border radius system
export const borderRadius = {
  none: 'rounded-none',
  sm: 'rounded-sm',     // 2px
  md: 'rounded-md',     // 6px
  lg: 'rounded-lg',     // 8px
  xl: 'rounded-xl',     // 12px
  full: 'rounded-full',
  // Component-specific
  components: {
    button: 'rounded-md',
    card: 'rounded-lg',
    badge: 'rounded-full',
    input: 'rounded-md',
    modal: 'rounded-lg',
  },
} as const;

// Shadow system
export const shadows = {
  none: 'shadow-none',
  sm: 'shadow-sm',
  md: 'shadow-md',
  lg: 'shadow-lg',
  xl: 'shadow-xl',
  // Component-specific
  components: {
    card: 'shadow-sm',
    cardHover: 'shadow-md',
    modal: 'shadow-lg',
    dropdown: 'shadow-md',
    button: 'shadow-sm',
    buttonHover: 'shadow-md',
  },
} as const;

// Legacy badge styles for backwards compatibility
export const badgeStyles = {
  success: badgeVariants.active.classes,
  warning: badgeVariants.pending.classes,
  error: badgeVariants.withdrawn.classes,
  info: badgeVariants.completed.classes,
  neutral: badgeVariants.inactive.classes,
  active: badgeVariants.active.classes,
  inactive: badgeVariants.inactive.classes,
} as const;